import { httpClient } from './httpClient';
import { Attachment } from '../components/tickets/TicketAttachments';
import { CommentAttachment } from '../types/comment';

export interface UploadAttachmentResponse {
  id: number;
  file_name: string;
  file_type: string | null;
  file_size: number | null;
  created_at: Date;
  file_path: string;
  uploaded_by_id: number;
}

export const attachmentService = {
  /**
   * Upload attachments for a ticket
   * @param ticketId The ID of the ticket
   * @param files The files to upload
   */
  async uploadAttachments(ticketId: number, files: File[]): Promise<Attachment> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    return httpClient.post<Attachment>(`/api/attachments/tickets/${ticketId}`, formData);
  },

  /**
   * Get all attachments for a ticket
   * @param ticketId The ID of the ticket
   */
  async getAttachments(ticketId: number): Promise<Attachment[]> {
    return httpClient.get<Attachment[]>(`/api/attachments/tickets/${ticketId}`);
  },

  /**
   * Download an attachment
   * @param attachmentId The ID of the attachment to download
   */
  /**
   * Get the download URL for an attachment
   * @param attachmentId The ID of the attachment to download
   * @returns A promise that resolves to the download URL
   */
  async downloadAttachment(attachmentId: number, fileName: string): Promise<void> {
    let response = await httpClient.get<{url: string}>(`/api/attachments/${attachmentId}/download`);
    const link = document.createElement('a');
    link.href = response.url;
    link.target = '_blank';
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  /**
   * Delete an attachment
   * @param attachmentId The ID of the attachment to delete
   */
  async deleteAttachment(attachmentId: number): Promise<void> {
    return httpClient.delete(`/api/attachments/${attachmentId}`);
  },

  /*
  * Comment related methods
  */

  /**
   * Get all attachments for a comment
   * @param ticketId - The ID of the ticket
   * @param commentId - The ID of the comment
   */
  async getCommentAttachments(ticketId: string | number, commentId: string | number): Promise<CommentAttachment[]> {
    return httpClient.get<CommentAttachment[]>(`/api/tickets/${ticketId}/comments/${commentId}/attachments`);
  },

  /**
   * Add attachments to a comment
   * @param ticketId - The ID of the ticket
   * @param commentId - The ID of the comment
   * @param files - The files to upload
   */
  async uploadCommentAttachments(ticketId: string | number, commentId: string | number, files: File[]): Promise<CommentAttachment> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    return httpClient.post<CommentAttachment>(`/api/tickets/${ticketId}/comments/${commentId}/attachments`, formData);
  }
};
